import { Button, MenuItem, Select as MuiSelect } from '@mui/material';
import { useField, Field } from 'formik';
import { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, Select } from '../../types/form-fields';
import { CheckboxField, FormRow, InputField } from '../fields';
import { FormBlock } from './common';

export const isSelectField = (field: FormField): field is Select => {
  return field.type === FieldType.Select;
};

export const defaultSelectFieldProps: Select = {
  type: FieldType.Select,
  placeholder: 'Placeholder',
  defaultValue: 'Option 1',
  options: 'Option 1, Option 2, Option 3, Option 4',
  required: true,
};

type SelectFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

const textValueSchema = Yup.string().optional().max(255, 'Should be < 255 characters');
const optionsSchema = Yup.string().optional().max(1023, 'Should be < 255 characters');

const SelectFieldSettings: FC<SelectFieldSettingsProps> = ({ name, remove }) => {
  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  const { t } = useTranslation();

  return (
    <SelectFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.placeholder`}
          component={InputField}
          label={t('pages.campaign.checkboxField.placeholder')}
          validate={(value: string) => validate(value, textValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.defaultValue`}
          component={InputField}
          label={t('pages.campaign.checkboxField.defaultValue')}
          validate={(value: string) => validate(value, textValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.options`}
          component={InputField}
          label={t('pages.campaign.checkboxField.options')}
          multiline
          maxRows={5}
          validate={(value: string) => validate(value, optionsSchema)}
          helperText={t('pages.campaign.checkboxField.optionsHelper')}
        />
      </FormRow>

      <FormRow>
        <Field name={`${name}.required`} component={CheckboxField} label={t('pages.campaign.checkboxField.required')} />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.name`}
          component={InputField}
          label={t('pages.campaign.checkboxField.name')}
          validate={(value: string) => validate(value, textValueSchema)}
          helperText={t('pages.campaign.checkboxField.nameInfo')}
        />
      </FormRow>

      {remove && (
        <Button color="error" onClick={remove}>
          {t('pages.campaign.checkboxField.buttonRemove')}
        </Button>
      )}
    </SelectFieldSettingsWrapper>
  );
};

type SelectFieldProps = {
  name: string;
  remove?: () => void;
};

export const SelectField: FC<SelectFieldProps> = ({ name, remove }) => {
  const [field] = useField<Select>(name);
  const dispatch = useAppDispatch();

  const { defaultValue, placeholder } = field.value;

  const handleClick = () => {
    dispatch(
      openSidebar({
        content: <SelectFieldSettings name={name} remove={remove} />,
      })
    );
  };

  const options = field.value.options
    .split(',')
    .map((option) => option.trim())
    .filter(Boolean);

  return (
    <FormBlock onClick={handleClick}>
      <StyledMuiSelect
        value={defaultValue.trim()}
        size="small"
        classes={{ select: 'select' }}
        displayEmpty
        renderValue={(value) => {
          if (!value) {
            return <em>{placeholder}</em>;
          }
          return value as string;
        }}
        disabled
      >
        <MenuItem disabled>
          <em>{placeholder}</em>
        </MenuItem>

        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </StyledMuiSelect>
    </FormBlock>
  );
};

const SelectFieldSettingsWrapper = styled.div``;

const StyledMuiSelect = styled(MuiSelect)`
  .select {
    background-color: #fff;
  }

  fieldset {
    border-color: #000 !important;
    border-width: 1px !important;
  }
`;
